import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDown, BookOpen, Compass, FileText, Scale } from "lucide-react";
import { Button } from "@/components/ax/Button";
import { BrandLogo } from "@/components/ax/BrandLogo";
import { LandingPricing } from "@/components/landing/LandingPricing";
import { LandingStory } from "@/components/landing/LandingStory";
import { LandingAttention } from "@/components/landing/LandingAttention";
import { LandingFaq } from "@/components/landing/LandingFaq";
import { HeroAbstract } from "@/components/landing/HeroAbstract";
import { LandingAmbient } from "@/components/landing/LandingAmbient";
import { getLandingExample } from "@/lib/landingExample";
import { RIGHTS_FOOTER_LABEL, RIGHTS_FOOTER_PATH } from "@/components/landing/rightsFooterLabel";
import { useI18n } from "@/i18n";
import styles from "@/components/landing/LandingVisual.module.css";

const STEPS = [
  { title: "landing.step1t", body: "landing.step1d" },
  { title: "landing.step2t", body: "landing.step2d" },
  { title: "landing.step3t", body: "landing.step3d" },
  { title: "landing.step4t", body: "landing.step4d" },
];
const LanguageFlag = ({ lang }) => lang === "id" ? (
  <svg viewBox="0 0 30 20" width="24" height="16" aria-hidden="true"><path fill="#fff" d="M0 0h30v20H0z" /><path fill="#ce1126" d="M0 0h30v10H0z" /></svg>
) : (
  <svg viewBox="0 0 60 40" width="24" height="16" aria-hidden="true"><path fill="#012169" d="M0 0h60v40H0z" /><path stroke="#fff" strokeWidth="8" d="m0 0 60 40M60 0 0 40" /><path stroke="#c8102e" strokeWidth="3" d="m0 0 60 40M60 0 0 40" /><path stroke="#fff" strokeWidth="13" d="M30 0v40M0 20h60" /><path stroke="#c8102e" strokeWidth="7" d="M30 0v40M0 20h60" /></svg>
);
const VALUES = [
  { title: "landing.visual.value1t", body: "landing.visual.value1d", note: "landing.visual.value1n", icon: FileText },
  { title: "landing.visual.value2t", body: "landing.visual.value2d", note: "landing.visual.value2n", icon: Scale },
  { title: "landing.visual.value3t", body: "landing.visual.value3d", note: "landing.visual.value3n", icon: Compass },
];

export default function Landing() {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const languageDisclosure = useRef(null);
  const ex = getLandingExample(lang);
  const changeLanguage = (nextLang) => {
    setLang(nextLang);
    languageDisclosure.current.open = false;
    languageDisclosure.current.querySelector("summary").focus();
  };

  return (
    <div className={styles.page} data-testid="landing-canvas">
      <a href="#konten" className="skip-link" data-testid="skip-link">{t("skip")}</a>
      <header className={styles.header} data-testid="landing-header">
        <div className={styles.headerInner}>
          <a href="#konten" aria-label="AXORIS" className={styles.brandLink}><BrandLogo tone="light" /></a>
          <nav aria-label={t("landing.visual.sectionNav")} className={styles.sectionNav}>
            <a href="#arsitektur-kognitif">{t("landing.visual.architecture")}</a>
            <a href="#nilai-operasional">{t("landing.visual.values")}</a>
            <a href="#proses">{t("landing.visual.process")}</a>
            <a href="#harga">{t("landing.pricing.nav")}</a>
          </nav>
          <nav aria-label={t("nav.main")} className={styles.headerActions}>
            <Button variant="ghost" onClick={() => navigate("/masuk")} data-testid="landing-signin-button" className={styles.signin}>
              {t("auth.signin")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/daftar")} data-testid="landing-signup-button" className={styles.headerSignup}>
              {t("landing.visual.trial")}
            </Button>
            <details ref={languageDisclosure} className={styles.languageSwitcher} data-testid="landing-language-switcher" onKeyDown={(event) => {
              if (event.key === "Escape") {
                languageDisclosure.current.open = false;
                languageDisclosure.current.querySelector("summary").focus();
              }
            }} onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false;
            }}>
              <summary aria-label={`${t("landing.language")}: ${t(`language.${lang}`)}`} title={t("landing.language")}><LanguageFlag lang={lang} /></summary>
              <div className={styles.languageOptions} role="group" aria-label={t("landing.language")}>
              <button type="button" aria-label={t("language.id")} title={t("language.id")} aria-pressed={lang === "id"} onClick={() => changeLanguage("id")}>
                <LanguageFlag lang="id" />
              </button>
              <button type="button" aria-label={t("language.en")} title={t("language.en")} aria-pressed={lang === "en"} onClick={() => changeLanguage("en")}>
                <LanguageFlag lang="en" />
              </button>
              </div>
            </details>
          </nav>
        </div>
      </header>

      <main id="konten" tabIndex={-1} data-testid="landing-page">
        <section className={styles.hero} aria-labelledby="landing-title" data-testid="landing-hero">
          <LandingAmbient variant="hero" />
          <div className={`${styles.container} ${styles.heroInner}`}>
            <p className={styles.heroEyebrow}>{t("landing.visual.eyebrow")}</p>
            <h1 id="landing-title" className={styles.title}>
              <span>Know what matters.</span><span>Decide what to do.</span>
            </h1>
            <p className={styles.heroLede}>{t("landing.visual.lede")}</p>
            <div className={styles.heroActions}>
              <Button variant="primary" onClick={() => navigate("/daftar")} data-testid="landing-cta-button">
                {t("landing.visual.trial")}
              </Button>
              <a href="#arsitektur-kognitif" data-testid="landing-cta-how" className={styles.heroHow}>
                {t("landing.hero.how")}<ArrowDown size={16} aria-hidden="true" />
              </a>
            </div>
            <HeroAbstract />
          </div>
        </section>

        <div className={styles.ambientRegion} data-testid="landing-ambient-region">
        <LandingAmbient />
        <LandingStory example={ex} />

        <section id="nilai-operasional" className={styles.values} aria-labelledby="landing-values" data-testid="landing-values">
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <p className={styles.eyebrow}>{t("landing.visual.values")}</p>
              <h2 id="landing-values">{t("landing.visual.valuesTitle")}</h2>
              <p className={styles.lede}>{t("landing.visual.valuesLede")}</p>
            </div>
            <ol className={styles.valueGrid}>
              {VALUES.map(({ title, body, note, icon: Icon }, index) => <li key={title} className={styles.valueCard} data-testid={`landing-value-${index + 1}`}>
                <div className={styles.valueTop}><Icon size={22} strokeWidth={1.5} aria-hidden="true" /><span>{String(index + 1).padStart(2, "0")}</span></div>
                <h3>{t(title)}</h3><p>{t(body)}</p><div className={styles.valueNote}>{t(note)}</div>
              </li>)}
            </ol>
            <LandingAttention example={ex} />
          </div>
        </section>

        <section id="proses" className={styles.process} aria-labelledby="landing-steps" data-testid="landing-process">
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <p className={styles.eyebrow}>{t("landing.visual.process")}</p><h2 id="landing-steps">{t("landing.steps")}</h2>
            </div>
            <div className={styles.processLayout}>
            <ol className={styles.stepGrid}>
              {STEPS.map((step, index) => <li key={step.title} className={styles.step}>
                <span className={styles.stepNumber} aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                <div><h3>{t(step.title)}</h3><p>{t(step.body)}</p></div>
              </li>)}
            </ol>
            <section className={styles.library} aria-labelledby="landing-library" data-testid="landing-library-section">
              <BookOpen size={24} strokeWidth={1.5} aria-hidden="true" /><div><h3 id="landing-library">{t("nav.library")}</h3><p>{t("landing.library")}</p></div>
            </section>
            </div>
          </div>
        </section>

        <LandingPricing onStartTrial={() => navigate("/daftar")} />

        <LandingFaq />

        <section id="akses" className={styles.closing} aria-labelledby="landing-principle" data-testid="landing-access">
          <div className={styles.container}>
            <p className={styles.eyebrow}>{t("landing.visual.access")}</p>
            <h2 id="landing-principle">{t("landing.principle")}</h2>
            <div className={styles.bottomAction}>
              <Button variant="primary" onClick={() => navigate("/daftar")} data-testid="landing-cta-bottom">{t("landing.visual.trial")}</Button>
            </div>
            <p className={styles.closingNote} data-testid="landing-access-note">{t("landing.closing.note")}</p>
          </div>
        </section>
        </div>
      </main>

      <footer className={styles.footer} data-testid="landing-footer">
        <div className={styles.container}>
          <div className={styles.footerGrid}>
            <div className={styles.footerBrand}>
              <BrandLogo tone="light" /><p>{t("landing.visual.footerLede")}</p>
              <a href="mailto:halo@axoris.id" data-testid="support-email">halo@axoris.id</a>
            </div>
            <nav aria-label={t("landing.visual.footerWorkspace")} className={styles.footerLinks}>
              <h2>{t("landing.visual.footerWorkspace")}</h2>
              <a href="#arsitektur-kognitif">{t("landing.visual.architecture")}</a>
              <a href="#nilai-operasional">{t("landing.visual.values")}</a>
              <a href="#proses">{t("landing.visual.process")}</a>
              <a href="#harga">{t("landing.pricing.nav")}</a>
              <a href="#pertanyaan">{t("landing.faq.nav")}</a>
              <a href="#akses">{t("landing.visual.access")}</a>
            </nav>
            <nav aria-label={t("landing.visual.footerPolicy")} className={styles.footerLinks}>
              <h2>{t("landing.visual.footerPolicy")}</h2>
              <a href="/kebijakan#privasi">{t("landing.visual.privacy")}</a>
              <a href="/kebijakan#syarat">{t("landing.visual.terms")}</a>
              <a href="/kebijakan#dpa">{t("landing.visual.dpa")}</a>
              <a href={RIGHTS_FOOTER_PATH} data-testid="landing-rights-link">{RIGHTS_FOOTER_LABEL[lang === "en" ? "en" : "id"]}</a>
            </nav>
          </div>
          <div className={styles.footerBottom}><span>© AXORIS</span><a href="/kebijakan" data-testid="landing-policy-link">{t("landing.policy")}</a></div>
        </div>
      </footer>
    </div>
  );
}
